import { FC, useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useMatchMedia } from '@/src/assets/hooks/useMatchMedia';
import { IHeader, IHeaderButton } from '@/src/assets/interfaces/headerInterface';
import SubMenu from '../subMenu/SubMenu';
import classNames from 'classnames';
import styles from './Nav.module.scss'
import close from '@/public/close.svg'
import NextImage from '../UI/nextImage/NextImage';
import Button from '../UI/button/Button';

type TData = {
    data: IHeader
    isNavActive: boolean
    headerButton: IHeaderButton
    setIsNavActive: (isNavActive: boolean) => void
    isMobileActive: string | null
    setIsMobileActive: (isActive: string | null) => void
}

const Nav: FC<TData> = ({isNavActive, data, setIsNavActive, headerButton, isMobileActive, setIsMobileActive}) => {

    const navRef = useRef<number | null>(null)
    const {isNarrowScreen} = useMatchMedia()
    const [isHovered, setIsHovered] = useState<string | null>(null);

    const navMobileClasslist = isNavActive && styles['nav__active'];

    const handleMouseEnter = (menuItemName: string) => {
        if (isNarrowScreen) return; 
        setIsHovered(menuItemName);
    };

    const handleMouseLeave = () => {
        if (isNarrowScreen) return; 
        setIsHovered(null)
    }

    const onHandleMobileNav = (menuItemName: string) => {
        if (!isNarrowScreen) return
        isMobileActive === menuItemName ? setIsMobileActive(null) : setIsMobileActive(menuItemName)
    }

    useEffect(() => {
        navRef.current = window.innerHeight
    }, [isNavActive]);


    useEffect(() => {
  
        const handleResize = () => {
           isNarrowScreen ? navRef.current = window.innerHeight : null
        }
        
        window.addEventListener('resize', handleResize);
        
        return () => {
         window.removeEventListener('resize', handleResize);
        };
        
      }, [isNavActive]);

    return (
        <nav 
            className={`${styles.nav} ${navMobileClasslist}`}
            style={{height: isNarrowScreen ? `${navRef.current}px` : 'auto'}}
        >
            <div className={styles.nav__container}>
                {
                    isNarrowScreen
                        &&  <button 
                                onClick={() => setIsNavActive(false)} 
                                aria-label='Close'
                                className={styles.close__button}
                            >
                                <NextImage 
                                    src={close}
                                    alt='Close'
                                    width={36}
                                    height={36}
                                />
                            </button>
                }
                <ul className={styles.nav__list}>
                    {
                        data.menu 
                            && data.menu.map((item, i) => {
                                const navClassName = classNames({
                                    [styles.show]: isHovered === item.menuItemName,
                                    [styles.hide]: isHovered !== item.menuItemName
                                });
                                const target = item.internalLink?.newWindow ? '_blank' : (item.externalUrl?.newWindow ? '_blank' : undefined)
                                return (
                                    <li key={`${item._key}--${i}`} 
                                        className={styles.nav__item}
                                        onMouseEnter={() => handleMouseEnter(item.menuItemName)}
                                        onMouseLeave={() => handleMouseLeave()}
                                        onClick={() => onHandleMobileNav(item.menuItemName)}
                                    >
                                        <div>
                                            <Link 
                                                href={item.internalLink?.link ? `${item.internalLink.link}` : (item.externalUrl?.url ?? '/')}
                                                target={target}
                                                rel={target ? "noopener noreferrer" : undefined}
                                                onClick={() => setIsNavActive(false)}
                                            >
                                                {item.menuItemName}
                                            </Link>
                                            {(isNarrowScreen && item.addSubMenu) 
                                                && <button aria-label='Expand' className={`${isMobileActive === item.menuItemName && styles.opened}`}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M16.25 7.5L10 13.75L3.75 7.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </button>}
                                        </div>
                                        
                                        { item.subMenu && (isHovered === item.menuItemName || isMobileActive === item.menuItemName) 
                                            && <SubMenu 
                                                    className={isHovered ? navClassName : ''} 
                                                    subMenu={item.subMenu!} 
                                                    isActive={isMobileActive === item.menuItemName}
                                                    setIsNavActive={setIsNavActive}
                                                />
                                        }
                                    </li>
                                )
                            }
                        )
                    }
                </ul>
                <ul className={styles.data__list}>
                    {
                        data.mobileMenu.map((item, i) => {
                            const target = item.internalLink?.newWindow ? '_blank' : (item.externalUrl?.newWindow ? '_blank' : undefined)
                            return (
                                <li key={`${item._key}__${i}`} className={styles.data__item}>
                                    <Link 
                                        href={item.internalLink?.link ? `/${item.internalLink.link}` : (item.externalUrl?.url ?? '/')}
                                        target={target}
                                        rel={target ? "noopener noreferrer" : undefined}
                                        onClick={() => setIsNavActive(false)}
                                    >
                                        {item.menuItemName}
                                    </Link>
                                </li>
                            )
                        })
                    }
                </ul>
            </div>

            <Button 
                    ariaLabel='Bonus Codes' 
                    className={styles.nav__btn}
                    buttonUrl={headerButton.buttonUrl}
                    target={data ? (headerButton.newWindow ?? undefined) : undefined}
                >
                    {headerButton.buttonText}
                </Button>
        </nav>
        
    )
}

export default Nav